import {
  Column,
  DateFilterModel,
  NumberFilterModel,
  SetFilterModel,
  TextFilterModel,
} from 'ag-grid-community';
import moment from 'moment';
import locale from '@glu/locale';

interface IFilterModels {
  text: TextFilterModel;
  date: DateFilterModel;
  number: NumberFilterModel;
  set: SetFilterModel;
}

interface IGetFilterChipParams<T extends keyof IFilterModels> {
  column: Column;
  field: string;
  columnDisplayName: string;
  filterModel: IFilterModels[T];
}

interface IGetFilterChipParamsCustom extends IGetFilterChipParams<'text'> {
  filterModel: any;
}

export type TCreateFilter<K extends keyof IFilterModels> = IFilterModels[K];

export type TFilter =
  | TCreateFilter<'date'>
  | TCreateFilter<'set'>
  | TCreateFilter<'number'>
  | TCreateFilter<'text'>;

const DISPLAY_DATE_FORMAT = 'YYYY-MM-DD';

export const getDateFilterChip = ({
  column,
  columnDisplayName,
  field,
  filterModel,
}: IGetFilterChipParams<'date'>) => {
  const { dateFrom, dateTo } = filterModel;
  const mDateFrom = moment(dateFrom);
  const mDateTo = dateTo ? moment(dateTo) : undefined;
  const format = column.getColDef().filterOptions?.componentProps?.format || DISPLAY_DATE_FORMAT;
  const criteriaDisplay =
    dateFrom !== dateTo && mDateTo
      ? `${mDateFrom.format(format)} ${locale.get('dataComponents.to')} ${mDateTo.format(format)}`
      : `${mDateFrom.format(format)}`;
  return {
    id: `${field}-inRange-${criteriaDisplay}`,
    nameDisplay: columnDisplayName,
    criteriaDisplay,
    field,
  };
};

export const getSetFilterChip = ({
  column,
  columnDisplayName,
  field,
  filterModel,
}: IGetFilterChipParams<'set'>) => {
  const { values } = filterModel;
  const columnDef = column.getColDef();
  return values.map((value) => {
    if (columnDef.filterOptions?.filterChipValueLabelMap) {
      return {
        id: `${field}-dropdown-${value}`,
        nameDisplay: columnDisplayName,
        criteriaDisplay:
          (value && columnDef.filterOptions.filterChipValueLabelMap[value]) || value!,
        field,
      };
    }
    return {
      id: `${field}-dropdown-${value}`,
      nameDisplay: columnDisplayName,
      criteriaDisplay: value!,
      field,
    };
  });
};

export const getNumberFilterChip = ({
  columnDisplayName,
  field,
  filterModel,
}: IGetFilterChipParams<'number'>) => {
  const { filter, filterTo, type } = filterModel;
  const criteriaDisplay =
    filter && filterTo
      ? locale.get('dataComponents.betweenCriteria', `${filter}`, `${filterTo}`)
      : `${locale.get(`dataComponents.${type}`)} ${filter}`;

  return {
    id: `${field}-${type}-${criteriaDisplay}`,
    nameDisplay: columnDisplayName,
    criteriaDisplay,
    field,
  };
};

export const getTextFilterChip = ({
  column,
  columnDisplayName,
  field,
  filterModel,
}: IGetFilterChipParams<'text'>) => {
  const columnDef = column.getColDef();
  const { filter, type } = filterModel;
  const criteriaDisplay =
    type === 'equals' ? filter! : `${locale.get(`dataComponents.${type}`)} ${filter}`;
  if (columnDef.filterOptions?.filterChipValueLabelMap) {
    return {
      id: `${field}-${type}-${criteriaDisplay}`,
      nameDisplay: columnDisplayName,
      criteriaDisplay:
        (filter && columnDef.filterOptions.filterChipValueLabelMap[filter]) || criteriaDisplay,
      field,
    };
  }
  return {
    id: `${field}-${type}-${criteriaDisplay}`,
    nameDisplay: columnDisplayName,
    criteriaDisplay,
    field,
  };
};

export const defaultGetCustomFilterChip = ({
  columnDisplayName,
  field,
  filterModel,
}: IGetFilterChipParamsCustom) => {
  const criteriaDisplay = `${locale.get('dataComponents.contains')} ${filterModel.filter}`;
  return {
    id: `${field}-contains-${criteriaDisplay}`,
    nameDisplay: columnDisplayName,
    criteriaDisplay,
    field,
  };
};
